import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    DataGrid,
    GridActionsCellItem,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat'; // For viewing messages
import Chip from '@mui/material/Chip';
import SettingsIcon from '@mui/icons-material/Settings'; // For settings button
import ChatTagsConfigModal from '../components/ChatTagsConfigModal'; // Import the new modal

import ChatGroupFormModal from '../components/ChatGroupFormModal';
import ChatMessagesModal from '../components/ChatMessagesModal';
// import MessageCommentsModal from '../components/MessageCommentsModal'; // Uncomment when created
import ConfirmDialog from '../components/ConfirmDialog';
import { useListChatGroups, useDeleteChatGroup } from '../services/appService';

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    } catch (e) {
        return 'Invalid Date';
    }
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function ChatGroupsPage() {
    const { appId } = useParams();
    const [chatGroups, setChatGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null); // null for create, group object for edit
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [messagesModalOpen, setMessagesModalOpen] = useState(false);
    const [selectedGroupForMessages, setSelectedGroupForMessages] = useState(null);
    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false); // State for the new modal
    // const [commentsModalOpen, setCommentsModalOpen] = useState(false); // For message comments
    // const [selectedMessageForComments, setSelectedMessageForComments] = useState(null);

    const listGroupsHook = useListChatGroups();
    const deleteGroupHook = useDeleteChatGroup();

    const fetchChatGroups = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listGroupsHook(appId);
            // Ensure tags is always an array
            const processedData = (data || []).map(group => ({
                ...group,
                tags: Array.isArray(group.tags) ? group.tags : [],
            }));
            setChatGroups(processedData);
        } catch (err) {
            console.error("Error fetching chat groups:", err);
            setError(err.message || 'Failed to load chat groups.');
        } finally {
            setLoading(false);
        }
    }, [appId, listGroupsHook]);

    useEffect(() => {
        fetchChatGroups();
    }, [fetchChatGroups]);

    const handleOpenCreateModal = () => {
        setEditingGroup(null);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (group) => {
        setEditingGroup(group);
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setEditingGroup(null);
    };

    const handleSaveGroup = () => {
        handleCloseFormModal();
        fetchChatGroups(); // Refresh list
    };

    const handleOpenConfirmDialog = (groupId) => {
        setGroupToDelete(groupId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setGroupToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!groupToDelete || !appId) return;
        setLoading(true);
        try {
            await deleteGroupHook(appId, groupToDelete);
            fetchChatGroups();
        } catch (err) {
            console.error("Error deleting chat group:", err);
            setError(err.message || 'Failed to delete chat group.');
            setLoading(false); // Ensure loading is turned off on error
        } finally {
            handleCloseConfirmDialog();
            // setLoading(false); // Already handled in error case
        }
    };

    const handleOpenMessagesModal = (group) => {
        setSelectedGroupForMessages(group);
        setMessagesModalOpen(true);
    };

    const handleCloseMessagesModal = () => {
        setSelectedGroupForMessages(null);
        setMessagesModalOpen(false);
    };

    const handleOpenTagsModal = () => {
        setIsTagsModalOpen(true);
    };

    const handleCloseTagsModal = () => {
        setIsTagsModalOpen(false);
    };

    // TODO: Implement comment modal logic when component exists
    // const handleOpenCommentsModal = (message) => { ... };
    // const handleCloseCommentsModal = () => { ... };

    const columns = [
        { field: 'id', headerName: 'ID', width: 150 },
        { field: 'name', headerName: 'Name', width: 250 },
        { field: 'type', headerName: 'Type', width: 120 },
        {
            field: 'display_source', // From chat_group_listings view
            headerName: 'Source/Org',
            width: 180,
            valueGetter: (params) => params?.row?.display_source || 'N/A',
        },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {params.value?.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" />
                    ))}
                </Box>
            ),
            sortable: false,
            filterable: false,
        },
        {
            field: 'is_active',
            headerName: 'Active',
            width: 80,
            type: 'boolean'
        },
        {
            field: 'is_pinned',
            headerName: 'Pinned',
            width: 80,
            type: 'boolean'
        },
        {
            field: 'last_message',
            headerName: 'Last Message',
            width: 250,
            valueGetter: (params) => params?.row?.last_message || '-',
            sortable: false,
        },
        {
            field: 'last_message_timestamp',
            headerName: 'Last Active',
            width: 180,
            type: 'dateTime',
            valueFormatter: (params) => formatDate(params?.value),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150, // Increased width for more actions
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                const actions = [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => handleOpenEditModal(row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<ChatIcon />}
                        label="View Messages"
                        onClick={() => handleOpenMessagesModal(row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleOpenConfirmDialog(id)}
                        color="inherit"
                    />,
                ];
                // Conditionally add "View Comments" for broadcast groups? (Might be better inside Messages Modal)
                // if (row.type === 'broadcast') {
                //    actions.splice(2, 0, 
                //        <GridActionsCellItem
                //            icon={<CommentIcon />}
                //            label="View Comments" // This needs refinement - comments are per message
                //            onClick={() => { /* Logic to open comments, likely needs message context */ }}
                //            color="inherit"
                //        />
                //    );
                // }
                return actions;
            },
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Chat Groups
                </Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={handleOpenTagsModal} // Use the new handler
                        sx={{ mr: 1 }}
                    >
                        Chat Tags
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                    >
                        Create Chat Group
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <DataGrid
                        rows={chatGroups}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 10 },
                            },
                            sorting: {
                                sortModel: [{ field: 'last_message_timestamp', sort: 'desc' }],
                            },
                        }}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        slots={{ toolbar: CustomToolbar }} // Use custom toolbar
                        slotProps={{
                             toolbar: { setFilterButtonEl: () => {} }, // Placeholder for potential custom logic
                        }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-main': {
                                height: '100%',
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                height: 'calc(100% - 56px - 56px)', // Adjust for header and toolbar
                                overflowY: 'auto'
                            },
                        }}
                    />
                </Box>
            )}

            <ChatGroupFormModal
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
                onSave={handleSaveGroup}
                group={editingGroup}
                appId={appId}
            />

            <ChatMessagesModal
                open={messagesModalOpen}
                onClose={handleCloseMessagesModal}
                appId={appId}
                group={selectedGroupForMessages}
                // onOpenComments={handleOpenCommentsModal} // Pass down comment handler
            />

            {/* 
            <MessageCommentsModal
                open={commentsModalOpen}
                onClose={handleCloseCommentsModal}
                appId={appId}
                groupId={selectedGroupForMessages?.id} // Need group context
                message={selectedMessageForComments}
            /> 
            */}

            <ChatTagsConfigModal
                open={isTagsModalOpen}
                onClose={handleCloseTagsModal}
                appId={appId}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete Chat Group?"
                message={`Are you sure you want to delete chat group ID: ${groupToDelete}? This action cannot be undone and might affect messages within the group.`}
            />
        </Box>
    );
}

export default ChatGroupsPage; 